import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';

// Non-lazy loaded components
import Login from './pages/Login';
import Layout from './components/Layout';
import ErrorFallback from './components/ErrorFallback';
import TokenValidator from './components/TokenValidator';

// Lazy loaded components with error boundaries
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const UserProfile = React.lazy(() => import('./components/UserProfile'));
const EngineChassisForm = React.lazy(() => import('./components/EngineChassisForm'));
const RegistrationForm = React.lazy(() => import('./components/RegistrationForm'));
const TerminationForm = React.lazy(() => import('./components/TerminationForm'));
const ContinuationForm = React.lazy(() => import('./components/ContinuationForm'));
const HypothecationHistory = React.lazy(() => import('./components/HypothecationHistory'));
const HypothecationForms = React.lazy(() => import('./components/HypothecationForms'));
const HypothecationRequests = React.lazy(() => import('./components/HypothecationRequests'));
const BankSettings = React.lazy(() => import('./components/BankSettings'));

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Caught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

// Loading component
const Loading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Protected route component to handle auth redirects
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, token } = useAuthStore();
  
  // Check both token and authentication state
  if (!isAuthenticated || !token) {
    return <Navigate to="/login" replace />;
  }
  
  return <TokenValidator>{children}</TokenValidator>;
};

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" />
      <ErrorBoundary>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <Dashboard />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="dashboard" element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <Dashboard />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="profile" element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <UserProfile />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="add-by-engine" element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <EngineChassisForm />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="add-by-registration" element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <RegistrationForm />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="terminate" element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <TerminationForm />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="continue" element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <ContinuationForm />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="history" element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <HypothecationHistory />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="hypothecation-forms" element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <HypothecationForms />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="hypothecation" element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <HypothecationRequests />
                </ErrorBoundary>
              </Suspense>
            } />
            <Route path="settings" element={
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <BankSettings />
                </ErrorBoundary>
              </Suspense>
            } />
          </Route>
          
          {/* Admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <Suspense fallback={<Loading />}>
                <ErrorBoundary>
                  <AdminDashboard />
                </ErrorBoundary>
              </Suspense>
            </ProtectedRoute>
          } />
          
          {/* Catch-all route - redirect to dashboard */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;